import { routerMiddleware } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import debounce from 'lodash-es/debounce';
import ReactGA from 'react-ga';
import { Tracker } from 'react-tracker';
import { applyMiddleware, compose, legacy_createStore as createStore } from 'redux';
import thunk from 'redux-thunk';
import trackListeners from '../infrastructure/middleware/analytics/listeners';
import trackingMiddleware from '../infrastructure/middleware/analytics/reduxTracking';
import { deleteState, loadState, saveState } from '../infrastructure/middleware/storage/reduxLocalStorage';
import { getResortLocationUrl } from '../utilities/resortLocation';
import createRootReducer from './reducers';
import { WRITE_LOCAL_STORAGE_DEBOUNCE_TIMEOUT, CLEAR_LOCAL_STORAGE_DEBOUNCE_TIMEOUT } from '../utilities/constants';

export const history = createBrowserHistory({
  basename: getResortLocationUrl()
});

// Set page on every router navigation, page view is triggered from GTM
history.listen(location => {
  ReactGA.set({ page: location.pathname });
});

// Set location and page on first load
ReactGA.set({
  location: window.location.href,
  page: window.location.pathname + window.location.search
});

const composeEnhancer =
  (window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
      trace: true, // Enabled tracing redux actions via redux devTools
      traceLimit: 25,
      actionSanitizer: action => (action.type?.startsWith('@@router/') ? { type: action.type } : action),
      stateSanitizer: state => {
        const { router, ...rest } = state; // Removed router related state from redux devTools
        return rest;
      }
    })) ||
  compose;

const tracker = new Tracker(trackListeners);

const middleware = [thunk, routerMiddleware(history), trackingMiddleware(tracker)];

// WARNING: This will override any values previously saved
// if property doesn't exist in the storage it will be created
const preloadedStatesDict = {
  'account.loginHasFailed': false,
  'account.loginErrorMessage': '',
  'account.creatingNewAccount': false,
  'account.newAccountHasSucceded': false,
  'account.newAccountValidationErrors': null,
  'account.newAccountErrorMessage': '',
  'plans.availabilityReqIsLoading': false,
  'plans.searchesGetReqIsLoading': false,
  'plans.searchesDelReqIsLoading': false,
  'plans.filtersApplied': {},
  'plans.filtersApplied.themed': false,
  'plans.filtersApplied.standard': false,
  'plans.filtersApplied.premium': false,
  'plans.filtersApplied.accessibility': false,
  'plans.sorting': 'rec',
  'plans.sortSuite': '',
  'plans.oldFiltersApplied': {},
  'plans.requiresQualifyingID': false,
  'plans.qualifyingIDError': false,
  'plans.isOpenRatesCalendar': false,
  'plans.availabilityHighestRatesRequestFailed': false,
  'plans.getAvRangeBySuiteIsLoading': false,
  'plans.getAvRangeBySuiteFailed': false,
  'plans.isBookingWidgetFocused': false,
  'plans.isErrorModalAlreadyClosed': false,
  'plans.availabilityErrorMessage': '',
  'plans.status': '',
  'plans.offerCodeError': {},
  'plans.availabilityHighestRatesIsLoading': false,
  'plans.availabilityHighestRatesList': [],
  'plans.availabilityList': [],
  'plans.emailCampaign': {},
  'plans.offerType': '',
  'plans.personalizationResponses': [],
  'plans.promoDescription': '',
  'plans.qualifyingID': '',
  'plans.recentSearchesList': [],
  'plans.selectedSuiteForCalendar': '',
  'plans.selectedSuiteForRange': '',
  'packages.isLoading': false,
  'packages.requestFailed': false,
  'packages.cabanasAvailabilityList': [],
  'packages.errorMessage': '',
  'packages.status': '',
  'birthdayParty.summaryData': {},
  'suite.getAvailabilityLoading': false,
  'suite.suiteRateMismatched': false,
  'suite.errorMessage': '',
  'suite.onSuiteSelectionError': '',
  'suite.responseHasPackages': false,
  'addApackage.addedPackages': {},
  'addApackage.flashMessage': '',
  'entities.deal.getErrorMessage': '',
  'entities.reservationDetails.cabanaRequestSuccess': false,
  'entities.reservationDetails.isCabanaLoading': false,
  'entities.reservationDetails.isLoading': false,
  'entities.reservationDetails.requestSuccess': false,
  'globalModal.isVisible': false,
  'globalModal.keyContentComponent': undefined,
  'globalModal.shouldUseGWComponent': false,
  'globalModal.params': {},
  'profile.isLoading': false,
  'profile.isUpdateSuccess': false,
  'profile.user': {},
  'profile.isUpdated': false,
  'offer.description': false,
  'offer.errorMessage': false,
  reservations: {},
  'lodgeConfig.configFetchIsLoading': false,
  lodgesLocations: {}
};

let persistedState = loadState(preloadedStatesDict);
export const localCreateStore = () => {
  const store = createStore(
    createRootReducer(history), // root reducer with router state
    persistedState,
    composeEnhancer(
      applyMiddleware(
        ...middleware
        // for dispatching history actions
        // ... other middlewares ...
      )
    )
  );

  return store;
};

export const store = localCreateStore();

store.subscribe(
  debounce(() => {
    deleteState();
    console.log('Storage deleted');
  }, CLEAR_LOCAL_STORAGE_DEBOUNCE_TIMEOUT)
);

store.subscribe(
  debounce(() => {
    const state = store.getState();
    const { router, ...stateToPersist } = state; // Removed router related state from being saved to local storage
    saveState(stateToPersist, preloadedStatesDict);
  }, WRITE_LOCAL_STORAGE_DEBOUNCE_TIMEOUT)
);
